import React, { useState } from 'react';
import { Music, Instagram, TrendingUp, BarChart3, Globe, ExternalLink, Play } from 'lucide-react';
import { translations } from '../../translations';
import { countries } from '../../data/countries';

interface MusicChartsProps {
  isDarkMode: boolean;
  language: string;
}

interface FormData {
  platform: 'instagram' | 'tiktok' | null;
  trackUrl: string;
  type: 'chart' | 'trending' | null;
  chartPosition: number | null;
  country: string;
}

export function MusicCharts({ isDarkMode, language }: MusicChartsProps) {
  const t = translations[language as keyof typeof translations];
  const [formData, setFormData] = useState<FormData>({
    platform: null,
    trackUrl: '',
    type: null,
    chartPosition: null,
    country: 'France'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePlatformSelect = (platform: 'instagram' | 'tiktok') => {
    setFormData(prev => ({ ...prev, platform }));
  };

  const handleTypeSelect = (type: 'chart' | 'trending') => {
    setFormData(prev => ({ 
      ...prev, 
      type,
      chartPosition: type === 'trending' ? null : prev.chartPosition
    }));
  };

  const handleChartPositionSelect = (position: number) => {
    setFormData(prev => ({ ...prev, chartPosition: position }));
  };

  const handleCountrySelect = (country: string) => {
    setFormData(prev => ({ ...prev, country }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    console.log('Form submitted:', formData);
    alert('Formulaire soumis avec succès !');
    
    // Reset form
    setFormData({
      platform: null,
      trackUrl: '',
      type: null,
      chartPosition: null,
      country: 'France'
    });
    setIsSubmitting(false);
  };

  const canSubmit = formData.platform && 
                   formData.trackUrl.trim() && 
                   formData.type && 
                   (formData.type === 'trending' || formData.chartPosition) &&
                   formData.country;

  return (
    <div className="p-8">
      <h1 className={`text-2xl font-semibold mb-6 ${
        isDarkMode ? 'text-gray-200' : 'text-gray-900'
      }`}>
        Music Charts
      </h1>

      <div className={`p-6 rounded-lg border ${
        isDarkMode 
          ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
          : 'bg-white border-[#e5e7eb]'
      }`}>
        <div className="space-y-8">
          {/* Platform Selection */}
          <div>
            <h3 className={`text-lg font-semibold mb-3 ${
              isDarkMode ? 'text-gray-200' : 'text-gray-900'
            }`}>
              Plateforme
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => handlePlatformSelect('instagram')}
                className={`p-4 rounded-lg border transition-all ${
                  formData.platform === 'instagram'
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10'
                    : isDarkMode
                      ? 'border-[#2b2b2b] hover:border-gray-600 bg-[#2b2b2b]'
                      : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                }`}
              >
                <Instagram size={24} className={`mx-auto mb-2 ${
                  formData.platform === 'instagram' ? 'text-orange-500' : 'text-gray-400'
                }`} />
                <div className={`text-sm font-medium ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-900'
                }`}>
                  Instagram
                </div>
              </button>

              <button
                onClick={() => handlePlatformSelect('tiktok')}
                className={`p-4 rounded-lg border transition-all ${
                  formData.platform === 'tiktok'
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10'
                    : isDarkMode
                      ? 'border-[#2b2b2b] hover:border-gray-600 bg-[#2b2b2b]'
                      : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                }`}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className={`mx-auto mb-2 ${
                  formData.platform === 'tiktok' ? 'text-orange-500' : 'text-gray-400'
                }`}>
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-.04-.1z"/>
                </svg>
                <div className={`text-sm font-medium ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-900'
                }`}>
                  TikTok
                </div>
              </button>
            </div>
          </div>

          {/* Track URL */}
          <div>
            <h3 className={`text-lg font-semibold mb-3 ${
              isDarkMode ? 'text-gray-200' : 'text-gray-900'
            }`}>
              Lien de votre track
            </h3>
            <div className="relative">
              <input
                type="url"
                value={formData.trackUrl}
                onChange={(e) => setFormData(prev => ({ ...prev, trackUrl: e.target.value }))}
                placeholder="https://open.spotify.com/track/..."
                className={`w-full rounded-lg pl-10 pr-4 py-3 text-sm ${
                  isDarkMode
                    ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                    : 'bg-gray-50 text-gray-900 border-gray-200'
                } border focus:outline-none focus:ring-2 focus:ring-orange-500`}
              />
              <ExternalLink size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            </div>
          </div>

          {/* Type Selection */}
          <div>
            <h3 className={`text-lg font-semibold mb-3 ${
              isDarkMode ? 'text-gray-200' : 'text-gray-900'
            }`}>
              Type de promotion
            </h3>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <button
                onClick={() => handleTypeSelect('chart')}
                className={`p-4 rounded-lg border transition-all ${
                  formData.type === 'chart'
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10'
                    : isDarkMode
                      ? 'border-[#2b2b2b] hover:border-gray-600 bg-[#2b2b2b]'
                      : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                }`}
              >
                <BarChart3 size={24} className={`mx-auto mb-2 ${
                  formData.type === 'chart' ? 'text-orange-500' : 'text-gray-400'
                }`} />
                <div className={`text-sm font-medium ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-900'
                }`}>
                  Chart
                </div>
              </button>

              <button
                onClick={() => handleTypeSelect('trending')}
                className={`p-4 rounded-lg border transition-all ${
                  formData.type === 'trending'
                    ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10'
                    : isDarkMode
                      ? 'border-[#2b2b2b] hover:border-gray-600 bg-[#2b2b2b]'
                      : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                }`}
              >
                <TrendingUp size={24} className={`mx-auto mb-2 ${
                  formData.type === 'trending' ? 'text-orange-500' : 'text-gray-400'
                }`} />
                <div className={`text-sm font-medium ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-900'
                }`}>
                  Trending
                </div>
              </button>
            </div>

            {/* Chart Position Selection */}
            {formData.type === 'chart' && (
              <div className="space-y-3">
                <h4 className={`text-sm font-medium ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Position souhaitée
                </h4>
                <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                  {[50, 40, 30, 20, 10, 5].map((position) => (
                    <button
                      key={position}
                      onClick={() => handleChartPositionSelect(position)}
                      className={`p-3 rounded-lg border transition-all ${
                        formData.chartPosition === position
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-500/10 text-orange-500'
                          : isDarkMode
                            ? 'border-[#2b2b2b] hover:border-gray-600 text-gray-200 bg-[#2b2b2b]'
                            : 'border-gray-200 hover:border-gray-300 text-gray-900 bg-gray-50'
                      }`}
                    >
                      <div className="text-lg font-bold">#{position}</div>
                      <div className="text-xs">Top {position}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Country Selection */}
          <div>
            <h3 className={`text-lg font-semibold mb-3 ${
              isDarkMode ? 'text-gray-200' : 'text-gray-900'
            }`}>
              Pays de promotion
            </h3>
            <div className="relative">
              <select
                value={formData.country}
                onChange={(e) => handleCountrySelect(e.target.value)}
                className={`w-full rounded-lg pl-10 pr-4 py-3 text-sm appearance-none ${
                  isDarkMode
                    ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                    : 'bg-gray-50 text-gray-900 border-gray-200'
                } border focus:outline-none focus:ring-2 focus:ring-orange-500`}
              >
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.flag} {country.name}
                  </option>
                ))}
              </select>
              <Globe size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                <svg className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          </div>

          {/* Summary and Submit */}
          <div className={`p-4 rounded-lg ${
            isDarkMode ? 'bg-[#2b2b2b]' : 'bg-gray-50'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              isDarkMode ? 'text-gray-200' : 'text-gray-900'
            }`}>
              Récapitulatif
            </h3>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className={`p-3 rounded-lg ${
                isDarkMode ? 'bg-[#1c1c1c]' : 'bg-white'
              }`}>
                <div className={`text-xs mb-1 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Plateforme
                </div>
                <div className={`font-semibold text-sm ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-900'
                }`}>
                  {formData.platform ? (formData.platform === 'instagram' ? 'Instagram' : 'TikTok') : '-'}
                </div>
              </div>

              <div className={`p-3 rounded-lg ${
                isDarkMode ? 'bg-[#1c1c1c]' : 'bg-white'
              }`}>
                <div className={`text-xs mb-1 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Track
                </div>
                <div className={`font-semibold text-sm truncate ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-900'
                }`}>
                  {formData.trackUrl || '-'}
                </div>
              </div>

              <div className={`p-3 rounded-lg ${
                isDarkMode ? 'bg-[#1c1c1c]' : 'bg-white'
              }`}>
                <div className={`text-xs mb-1 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Type
                </div>
                <div className={`font-semibold text-sm ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-900'
                }`}>
                  {formData.type === 'chart' && formData.chartPosition ? `Chart #${formData.chartPosition}` : 
                   formData.type === 'trending' ? 'Trending' : '-'}
                </div>
              </div>

              <div className={`p-3 rounded-lg ${
                isDarkMode ? 'bg-[#1c1c1c]' : 'bg-white'
              }`}>
                <div className={`text-xs mb-1 ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-600'
                }`}>
                  Pays
                </div>
                <div className={`font-semibold text-sm ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-900'
                }`}>
                  {countries.find(c => c.code === formData.country)?.flag} {formData.country}
                </div>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
              className="w-full bg-orange-500 text-white rounded-lg py-3 font-medium hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  <Play size={16} />
                  Lancer la promotion
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}